/**
 * Delivery history of one document's emails, or of one customer's (#3787).
 *
 * Reads GET /outbound-emails one bounded page at a time (limit + offset, so
 * every older row stays reachable through "show more"), and follows the
 * Resend webhook live (useOutboundEmails). Each state is announced: loading
 * politely, a failed load as an alert with a retry, and no rows as the plain
 * "no delivery evidence" status (or the caller's "nothing was emailed" text),
 * never as a success.
 */
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { ApiError } from '@/lib/api';
import { useOutboundEmails, type OutboundEmailSubjectType } from '@/hooks/useOutboundEmails';
import { EmailDeliveryStatus } from './EmailDeliveryStatus';

/** The document an email carried, named in a list that mixes them (#4187). */
const DOCUMENT_LABEL_KEYS: Record<string, string> = {
  invoice: 'emailDelivery.document.invoice',
  receipt: 'emailDelivery.document.receipt',
  quote: 'emailDelivery.document.quote',
  work_order_offer: 'emailDelivery.document.work_order_offer',
  booking: 'emailDelivery.document.booking',
  inspection_report: 'emailDelivery.document.inspection_report',
};

interface EmailDeliveryHistoryProps {
  entityType: OutboundEmailSubjectType;
  entityId: string | undefined;
  /** Heading text, already translated. Defaults to the shared title. */
  title?: string;
  /** The surrounding section already carries the heading; it still labels this one. */
  hideTitle?: boolean;
  /**
   * Text for no rows when that means nothing was emailed, already translated
   * (#4187). Without it no rows is the "no delivery evidence" status, for a
   * document known to have been emailed.
   */
  emptyText?: string;
  /** Name the document each email carried, for a list that mixes them (#4187). */
  showDocument?: boolean;
}

export function EmailDeliveryHistory({
  entityType,
  entityId,
  title,
  hideTitle = false,
  emptyText,
  showDocument = false,
}: EmailDeliveryHistoryProps) {
  const { t } = useTranslation('common');
  const headingId = useId();
  const query = useOutboundEmails(entityType, entityId);

  const heading = title ?? t('emailDelivery.title');
  const forbidden = query.error instanceof ApiError && query.error.status === 403;
  const emails = query.data?.emails ?? [];

  const retry = () => void (query.isFetchNextPageError ? query.fetchNextPage() : query.refetch());
  const loading = (
    <Box role="status" aria-live="polite" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <CircularProgress size={16} aria-hidden="true" />
      <Typography variant="body2">{t('emailDelivery.loading')}</Typography>
    </Box>
  );

  // A failed "show more" or refetch keeps the rows already read above its alert.
  const errorAlert = query.isError ? (
    <Alert
      severity="error"
      sx={{ mt: emails.length > 0 ? 1 : 0 }}
      action={
        forbidden ? undefined : (
          <Button color="inherit" size="small" onClick={retry}>
            {t('emailDelivery.retry')}
          </Button>
        )
      }
    >
      {t(forbidden ? 'emailDelivery.forbidden' : 'emailDelivery.loadError')}
    </Alert>
  ) : null;

  let body;
  if (query.isPending && !query.isError) {
    body = loading;
  } else if (emails.length > 0) {
    body = (
      <Stack component="ul" spacing={1.5} sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {emails.map((email) => (
          <li key={email.id}>
            {showDocument && email.entityType && DOCUMENT_LABEL_KEYS[email.entityType] ? (
              <Typography variant="caption" component="p" sx={{ fontWeight: 600, m: 0 }}>
                {t(DOCUMENT_LABEL_KEYS[email.entityType] ?? '')}
              </Typography>
            ) : null}
            <EmailDeliveryStatus email={email} />
          </li>
        ))}
      </Stack>
    );
  } else if (!query.isError) {
    body = emptyText ? (
      <Typography variant="body2" color="text.secondary" data-testid="email-delivery-empty">
        {emptyText}
      </Typography>
    ) : (
      <EmailDeliveryStatus email={null} />
    );
  }

  return (
    <Box
      component="section"
      data-testid="email-delivery-history"
      {...(hideTitle ? { 'aria-label': heading } : { 'aria-labelledby': headingId })}
    >
      {hideTitle ? null : (
        <Typography id={headingId} variant="subtitle2" component="h3" sx={{ fontWeight: 600, mb: 1 }}>
          {heading}
        </Typography>
      )}
      {body}
      {errorAlert}
      {query.isFetchingNextPage ? <Box sx={{ mt: 1 }}>{loading}</Box> : null}
      {query.hasNextPage && !query.isFetchNextPageError ? (
        <Button size="small" sx={{ mt: 1 }} disabled={query.isFetching} onClick={() => void query.fetchNextPage()}>
          {t('emailDelivery.showMore')}
        </Button>
      ) : null}
    </Box>
  );
}
